import { useState, useEffect, useMemo } from "react";
import {
  get_member_attendance_summary,
  get_staff_summary,
  getCurrentUserRoleService,
  getStudentLeavesService,
  getStaffLeavesService,
  getHolidaysByMonth, 
  getDateExceptionsService 
} from "services/api-service";

export default function AttendanceReportModal() {
  const today = new Date();
  const currentMonthStr = today.toISOString().split("T")[0].substring(0, 7);
  const currentYear = today.getFullYear().toString();
  const currentQuarter = `Q${Math.floor(today.getMonth() / 3) + 1}`;

  const [step, setStep] = useState("initial");
  const [reportType, setReportType] = useState(null);
  
  // Timeframe Selections
  const [timeframe, setTimeframe] = useState("monthly"); // monthly, quarterly, yearly
  const [selectedMonth, setSelectedMonth] = useState(currentMonthStr);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState(currentQuarter);
  
  const [persons, setPersons] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [allRangeData, setAllRangeData] = useState({});
  
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0); 
  const [hoveredDay, setHoveredDay] = useState(null);
  const [userRole, setUserRole] = useState(null);
  
  const [holidays, setHolidays] = useState({});
  const [dateExceptions, setDateExceptions] = useState({});
  const [studentLeaves, setStudentLeaves] = useState([]); 
  const [staffLeaves, setStaffLeaves] = useState([]); 

  useEffect(() => {
    getCurrentUserRoleService()
      .then((res) => setUserRole(res.data.role_type))
      .catch((err) => console.error("Error fetching user role:", err));
  }, []);

  const getDatesForTimeframe = () => {
    let start, end;
    const year = parseInt(selectedYear);
    
    if (timeframe === "monthly") {
      const [mYear, mMonth] = selectedMonth.split("-");
      start = new Date(mYear, parseInt(mMonth) - 1, 1);
      end = new Date(mYear, parseInt(mMonth), 0);
    } else if (timeframe === "quarterly") {
      const qStartMonth = (parseInt(selectedQuarter.replace("Q", "")) - 1) * 3;
      start = new Date(year, qStartMonth, 1);
      end = new Date(year, qStartMonth + 3, 0);
    } else {
      start = new Date(year, 0, 1);
      end = new Date(year, 11, 31);
    }

    if (end > today) end = today;

    const dates = [];
    let curr = new Date(start);
    while (curr <= end) {
      const localDate = new Date(curr.getTime() - curr.getTimezoneOffset() * 60000).toISOString().split("T")[0];
      dates.push(localDate);
      curr.setDate(curr.getDate() + 1);
    }
    return dates;
  };

  const fetchHolidaysAndExceptions = async (dates) => {
    const uniqueMonths = [...new Set(dates.map(d => d.substring(0, 7)))];
    let newHolidays = { ...holidays };
    let newExceptions = { ...dateExceptions };

    for (const ym of uniqueMonths) {
      const [year, month] = ym.split("-");
      try {
        const res = await getHolidaysByMonth(parseInt(year), parseInt(month));
        if (res?.data?.holidays) {
          res.data.holidays.forEach(h => { if (h.date) newHolidays[h.date] = h; });
        }
      } catch (e) {}
    }
    setHolidays(newHolidays);

    const CHUNK_SIZE = 15;
    for (let i = 0; i < dates.length; i += CHUNK_SIZE) {
      const chunk = dates.slice(i, i + CHUNK_SIZE);
      const results = await Promise.allSettled(chunk.map(d => getDateExceptionsService(d)));
      results.forEach((result, idx) => {
        if (result.status === "fulfilled" && result.value?.data) {
          newExceptions[chunk[idx]] = result.value.data;
        }
      });
    }
    setDateExceptions(newExceptions);
  };

  const fetchAttendanceDataBatched = async (type, dates) => {
    const allRecords = [];
    const CHUNK_SIZE = 12; 
    let completed = 0;

    for (let i = 0; i < dates.length; i += CHUNK_SIZE) {
      const chunk = dates.slice(i, i + CHUNK_SIZE);
      const promises = chunk.map(d => 
        type === "student" 
          ? get_member_attendance_summary({ date: d, page_number: 1, page_size: 5000 })
          : get_staff_summary({ date: d, page_number: 1, page_size: 5000 })
      );

      const results = await Promise.allSettled(promises);
      results.forEach(res => {
        if (res.status === "fulfilled" && res.value?.data) {
          allRecords.push(...(res.value.data.data || res.value.data));
        }
      });
      
      completed += chunk.length;
      setProgress(Math.round((completed / dates.length) * 100));
    }
    return allRecords;
  };

  const handleReportTypeSelect = async (type) => {
    setReportType(type);
    setLoading(true);
    setProgress(0);

    try {
      const dates = getDatesForTimeframe();
      if (dates.length === 0) {
        alert("No valid dates found in the selected range.");
        setLoading(false);
        return;
      }

      await fetchHolidaysAndExceptions(dates);

      if (type === "student") {
        try { const res = await getStudentLeavesService(); setStudentLeaves(res.data?.data || res.data || []); } catch (e) {}
      } else {
        try { const res = await getStaffLeavesService(); setStaffLeaves(res.data?.data || res.data || []); } catch (e) {}
      }

      const cacheKey = `${type}-${timeframe}-${selectedMonth}-${selectedQuarter}-${selectedYear}`;
      let allRecords = allRangeData[cacheKey];

      if (!allRecords) {
        allRecords = await fetchAttendanceDataBatched(type, dates);
        setAllRangeData(prev => ({ ...prev, [cacheKey]: allRecords }));
      }

      const uniquePersonsMap = new Map();
      allRecords.forEach((r) => {
        const id = type === "student" ? r.member_id : r.staff_id;
        const name = type === "student" ? r.member_name : r.staff_name;
        if (id && !uniquePersonsMap.has(id)) uniquePersonsMap.set(id, { id, name: name || "Unknown" });
      });

      const uniquePersons = Array.from(uniquePersonsMap.values());
      if (uniquePersons.length === 0) {
        alert(`No records found for the selected ${timeframe} period.`);
        setLoading(false);
        return;
      }

      setPersons(uniquePersons.sort((a, b) => a.name.localeCompare(b.name)));
      setStep("selectPerson");
    } catch (error) {
      alert("Failed to load list. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePersonSelect = (person) => {
    setSelectedPerson(person);
    setStep("reportView");
    generateReport(person);
  };

  const generateReport = async (person) => {
    setLoading(true);
    try {
      const dates = getDatesForTimeframe();
      const cacheKey = `${reportType}-${timeframe}-${selectedMonth}-${selectedQuarter}-${selectedYear}`;
      const allRecords = allRangeData[cacheKey];

      let present = 0;
      let absent = 0;
      const dailyRecords = {};

      const personRecords = allRecords.filter((r) => {
        const idMatch = reportType === "student" ? String(r.member_id) === String(person.id) : String(r.staff_id) === String(person.id);
        return idMatch;
      });

      const recordsByDate = {};
      personRecords.forEach((r) => {
        const dateStr = r.date || r.attendance_date || r.created_at;
        if (dateStr) recordsByDate[dateStr.split("T")[0]] = r;
      });

      dates.forEach(dateStr => {
        const isHoliday = !!holidays[dateStr];
        const record = recordsByDate[dateStr];

        let isOnLeave = false;
        let leaveReason = "";

        const leaves = reportType === "student" ? studentLeaves : staffLeaves;
        const matchingLeave = leaves.find((l) => {
          const lStart = l.start_date?.split("T")[0];
          const lEnd = l.end_date?.split("T")[0];
          const lId = reportType === "student" ? l.student_id : l.staff_id;
          return String(lId) === String(person.id) && dateStr >= lStart && dateStr <= lEnd;
        });

        if (matchingLeave) { isOnLeave = true; leaveReason = matchingLeave.reason; }

        if (isOnLeave) {
          dailyRecords[dateStr] = { status: "On Leave", isHoliday: false, isOnLeave: true, leaveReason: leaveReason || "Approved Leave" };
        } else if (isHoliday) {
          dailyRecords[dateStr] = { status: "Holiday", isHoliday: true, isOnLeave: false, holidayName: holidays[dateStr]?.name };
        } else if (record) {
          dailyRecords[dateStr] = {
            status: record.status,
            time: record.check_in_time || record.recognition_time,
            checkIn: record.check_in_time,
            checkOut: record.check_out_time,
            isHoliday: false,
            isOnLeave: false,
          };
          if (record.status === "Present") present++;
          else if (record.status === "Absent") absent++;
        } else {
          dailyRecords[dateStr] = { status: "No Data", isHoliday: false, isOnLeave: false };
        }
      });

      setReportData({
        present,
        absent,
        totalDays: dates.length,
        percentage: dates.length > 0 ? Math.round((present / dates.length) * 100) : 0,
        dailyRecords,
        dates
      });
    } catch (error) {
      alert("Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "—";
    if (timeString.includes("T")) return timeString.split("T")[1].substring(0, 5);
    return timeString.substring(0, 5);
  };

  const getExportDetails = () => {
    let titleStr = reportType === "student" ? "MEMBER ATTENDANCE REPORT" : "STAFF ATTENDANCE REPORT";
    let periodStr = timeframe === "monthly" ? selectedMonth : timeframe === "quarterly" ? `${selectedQuarter} ${selectedYear}` : `${selectedYear}`;
    return { titleStr, periodStr };
  };

  const downloadReportAsExcel = async () => {
    if (!reportData || !selectedPerson) return;
    try {
      const XLSX = await import("xlsx");
      const { titleStr, periodStr } = getExportDetails();
      const wsData = [];

      wsData.push([titleStr]);
      wsData.push([]);
      wsData.push(["NAME:", selectedPerson.name]);
      wsData.push(["ID:", selectedPerson.id]);
      wsData.push(["PERIOD:", periodStr]);
      wsData.push([]);

      const headers = ["Date", "Status", "In Time", "Out Time", "Notes"];
      wsData.push(headers);

      reportData.dates.forEach(dateStr => {
        const record = reportData.dailyRecords[dateStr];
        let status, notes;
        if (record?.isOnLeave) {
          status = "Leave"; notes = `On Leave: ${record.leaveReason}`;
        } else if (record?.isHoliday) {
          status = "Holiday"; notes = record.holidayName ? `Holiday: ${record.holidayName}` : "Holiday";
        } else {
          status = record?.status === "Present" ? "Present" : (record?.status === "Absent" ? "Absent" : "-");
          notes = "";
        }
        const inTime = record?.checkIn && !record?.isHoliday && !record?.isOnLeave ? formatTime(record.checkIn) : "-";
        const outTime = record?.checkOut && !record?.isHoliday && !record?.isOnLeave ? formatTime(record.checkOut) : "-";

        wsData.push([dateStr, status, inTime, outTime, notes]);
      });

      wsData.push([]);
      wsData.push(["SUMMARY"]);
      wsData.push(["Total Evaluated Days:", reportData.totalDays]);
      wsData.push(["Present (P):", reportData.present]);
      wsData.push(["Absent (A):", reportData.absent]);

      const worksheet = XLSX.utils.aoa_to_sheet(wsData);
      worksheet["!cols"] = [{ wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 30 }];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
      XLSX.writeFile(workbook, `${reportType}_Report_${selectedPerson.id}_${periodStr}.xlsx`);
    } catch (error) {
      alert("Failed to download Excel file");
    }
  };

  const handleBack = () => {
    if (step === "reportView") {
      setStep("selectPerson");
      setSelectedPerson(null);
      setReportData(null);
    } else if (step === "selectPerson") {
      setStep("initial");
      setReportType(null);
      setPersons([]);
    }
  };

  const groupedDates = useMemo(() => {
    if (!reportData || !reportData.dates) return {};
    return reportData.dates.reduce((acc, dateStr) => {
      const dateObj = new Date(dateStr);
      const monthLabel = dateObj.toLocaleString('en-US', { month: 'short', year: 'numeric' }).toUpperCase();
      if (!acc[monthLabel]) acc[monthLabel] = [];
      acc[monthLabel].push(dateStr);
      return acc;
    }, {});
  }, [reportData]);

  if (step === "initial") {
    return (
      <div style={{ padding: "24px", minHeight: "100vh", backgroundColor: "#f8fafc" }}>
        <div style={{ maxWidth: "650px", margin: "0 auto", backgroundColor: "white", padding: "32px", borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
          <h1 style={{ fontSize: "28px", fontWeight: "700", color: "#0f172a", marginBottom: "8px" }}>Analytics & Reports</h1>
          <p style={{ color: "#64748b", fontSize: "15px", marginBottom: "32px" }}>Generate comprehensive attendance reports for Members and Staff across custom timeframes.</p>

          <div style={{ display: "flex", gap: "8px", marginBottom: "24px", backgroundColor: "#f1f5f9", padding: "6px", borderRadius: "10px" }}>
            {['monthly', 'quarterly', 'yearly'].map((tf) => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "none", cursor: "pointer", fontWeight: "600", fontSize: "14px", transition: "all 0.2s",
                         backgroundColor: timeframe === tf ? "white" : "transparent", color: timeframe === tf ? "#1e40af" : "#64748b",
                         boxShadow: timeframe === tf ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}
              >
                {tf.charAt(0).toUpperCase() + tf.slice(1)}
              </button>
            ))}
          </div>

          <div style={{ marginBottom: "32px", backgroundColor: "#f8fafc", padding: "20px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
            {timeframe === "monthly" && (
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", color: "#475569", fontSize: "13px", textTransform: "uppercase" }}>Select Month</label>
                <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} style={{ width: "100%", padding: "12px 16px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "15px", outline: "none", color: "#0f172a", fontWeight: "500" }} />
              </div>
            )}
            {timeframe === "quarterly" && (
              <div style={{ display: "flex", gap: "16px" }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", color: "#475569", fontSize: "13px", textTransform: "uppercase" }}>Year</label>
                  <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} style={{ width: "100%", padding: "12px 16px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "15px", outline: "none", color: "#0f172a", fontWeight: "500", backgroundColor: "white" }}>
                    {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", color: "#475569", fontSize: "13px", textTransform: "uppercase" }}>Quarter</label>
                  <select value={selectedQuarter} onChange={(e) => setSelectedQuarter(e.target.value)} style={{ width: "100%", padding: "12px 16px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "15px", outline: "none", color: "#0f172a", fontWeight: "500", backgroundColor: "white" }}>
                    {['Q1', 'Q2', 'Q3', 'Q4'].map(q => <option key={q} value={q}>{q} (3 Months)</option>)}
                  </select>
                </div>
              </div>
            )}
            {timeframe === "yearly" && (
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", color: "#475569", fontSize: "13px", textTransform: "uppercase" }}>Select Year</label>
                <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} style={{ width: "100%", padding: "12px 16px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "15px", outline: "none", color: "#0f172a", fontWeight: "500", backgroundColor: "white" }}>
                  {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: userRole === "IT_ADMIN" ? "1fr 1fr" : "1fr", gap: "16px" }}>
            <button
              onClick={() => handleReportTypeSelect("student")} disabled={loading}
              style={{ padding: "20px", background: loading ? "#f8fafc" : "white", border: "2px solid #e2e8f0", borderRadius: "12px", cursor: loading ? "not-allowed" : "pointer", textAlign: "center", transition: "all 0.2s" }}
              onMouseOver={(e) => { if (!loading) { e.currentTarget.style.borderColor = "#1e40af"; e.currentTarget.style.backgroundColor = "#eff6ff"; } }}
              onMouseOut={(e) => { if (!loading) { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.backgroundColor = "white"; } }}
            >
              <div style={{ fontSize: "32px", marginBottom: "12px" }}>👥</div>
              <div style={{ fontWeight: "700", color: "#0f172a", fontSize: "16px" }}>Member Report</div>
              <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>Gym member attendance</div>
            </button>

            {userRole === "IT_ADMIN" && (
              <button
                onClick={() => handleReportTypeSelect("staff")} disabled={loading}
                style={{ padding: "20px", background: loading ? "#f8fafc" : "white", border: "2px solid #e2e8f0", borderRadius: "12px", cursor: loading ? "not-allowed" : "pointer", textAlign: "center", transition: "all 0.2s" }}
                onMouseOver={(e) => { if (!loading) { e.currentTarget.style.borderColor = "#10b981"; e.currentTarget.style.backgroundColor = "#ecfdf5"; } }}
                onMouseOut={(e) => { if (!loading) { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.backgroundColor = "white"; } }}
              >
                <div style={{ fontSize: "32px", marginBottom: "12px" }}>👔</div>
                <div style={{ fontWeight: "700", color: "#0f172a", fontSize: "16px" }}>Staff Attendance</div>
                <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>Workforce time tracking</div>
              </button>
            )}
          </div>

          {loading && (
            <div style={{ marginTop: '32px', textAlign: 'center' }}>
              <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Fetching securely... {progress}%
              </div>
              <div style={{ width: '100%', height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ width: `${progress}%`, height: '100%', backgroundColor: reportType === 'student' ? '#1e40af' : '#10b981', borderRadius: '4px', transition: 'width 0.3s ease-out' }}></div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (step === "selectPerson") {
    return (
      <div style={{ padding: "24px", minHeight: "100vh", backgroundColor: "#f8fafc" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <button onClick={handleBack} style={{ padding: "8px 16px", background: "white", color: "#475569", border: "1px solid #cbd5e1", borderRadius: "6px", cursor: "pointer", fontWeight: "600", fontSize: "13px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "6px", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
            ← Change Period
          </button>
          
          <h1 style={{ fontSize: "24px", fontWeight: "700", color: "#0f172a", marginBottom: "8px" }}>
            Select {reportType === "student" ? "Member" : "Employee"}
          </h1>
          <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "24px" }}>
            Timeframe: <strong>{timeframe === "monthly" ? selectedMonth : timeframe === "quarterly" ? `${selectedQuarter} ${selectedYear}` : selectedYear}</strong>
          </p>

          <div style={{ backgroundColor: "white", borderRadius: "12px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <div style={{ overflowY: "auto", maxHeight: "650px" }}>
              {persons.map((person, idx) => (
                <button
                  key={person.id} onClick={() => handlePersonSelect(person)}
                  style={{ width: "100%", padding: "16px 20px", textAlign: "left", border: "none", borderBottom: idx < persons.length - 1 ? "1px solid #e2e8f0" : "none", backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f8fafc", cursor: "pointer", transition: "background-color 0.1s" }}
                  onMouseOver={(e) => { e.currentTarget.style.backgroundColor = "#eff6ff"; }}
                  onMouseOut={(e) => { e.currentTarget.style.backgroundColor = idx % 2 === 0 ? "#ffffff" : "#f8fafc"; }}
                >
                  <div style={{ fontWeight: "700", color: "#0f172a", fontSize: "15px" }}>{person.name}</div>
                  <div style={{ color: "#64748b", fontSize: "12px", marginTop: "4px", fontWeight: "500" }}>ID: {person.id}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === "reportView" && reportData) {
    const isMember = reportType === "student";
    const { periodStr } = getExportDetails();
    const useHeatmap = timeframe === "quarterly" || timeframe === "yearly";

    return (
      <div style={{ padding: "24px", backgroundColor: "#f8fafc", minHeight: "100vh" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <button onClick={handleBack} style={{ padding: "8px 16px", background: "white", color: "#475569", border: "1px solid #cbd5e1", borderRadius: "6px", cursor: "pointer", fontWeight: "600", fontSize: "13px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "6px", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
            ← Back to List
          </button>

          {!loading && (
            <>
              <div style={{ backgroundColor: "white", padding: "32px", borderRadius: "16px", marginBottom: "24px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "20px" }}>
                  <div>
                    <h1 style={{ fontSize: "32px", fontWeight: "800", color: "#0f172a", margin: "0 0 12px 0", display: "flex", alignItems: "center", gap: "12px" }}>
                      <span style={{ backgroundColor: isMember ? "#eff6ff" : "#ecfdf5", color: isMember ? "#1e40af" : "#047857", padding: "8px", borderRadius: "12px", fontSize: "24px" }}>
                         {isMember ? "👥" : "👔"}
                      </span>
                      {selectedPerson?.name}
                    </h1>
                    <p style={{ color: "#64748b", fontSize: "15px", margin: 0, fontWeight: "500" }}>
                      ID: <strong>{selectedPerson?.id}</strong> <span style={{ margin: "0 8px" }}>|</span> Type: <strong>{isMember ? "Member" : "Employee"}</strong>
                    </p>
                  </div>
                  <div style={{ textAlign: "right", padding: "12px 20px", backgroundColor: "#f8fafc", borderRadius: "12px", border: "1px solid #cbd5e1" }}>
                    <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>Reporting Period</div>
                    <div style={{ fontSize: "20px", fontWeight: "800", color: "#0f172a" }}>{periodStr}</div>
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px", marginBottom: "24px" }}>
                <div style={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderLeft: "4px solid #10b981", padding: "24px", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "8px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Days Present
                  </div>
                  <div style={{ fontSize: "42px", fontWeight: "800", color: "#10b981", lineHeight: "1" }}>{reportData.present}</div>
                </div>

                {/* Always render absent box for both members and staff */}
                <div style={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderLeft: "4px solid #ef4444", padding: "24px", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "8px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Days Absent</div>
                  <div style={{ fontSize: "42px", fontWeight: "800", color: "#ef4444", lineHeight: "1" }}>{reportData.absent}</div>
                </div>

                <div style={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderLeft: "4px solid #3b82f6", padding: "24px", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "8px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Evaluated Days</div>
                  <div style={{ fontSize: "42px", fontWeight: "800", color: "#3b82f6", lineHeight: "1" }}>{reportData.totalDays}</div>
                </div>
              </div>

              <div style={{ display: "flex", gap: "12px", marginBottom: "24px", flexWrap: "wrap" }}>
                <button onClick={downloadReportAsExcel} style={{ padding: "12px 24px", backgroundColor: "#0f172a", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "700", fontSize: "14px", display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }}>
                  📊 Export Excel Report
                </button>
              </div>

              <div style={{ backgroundColor: "white", borderRadius: "16px", padding: "32px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", marginBottom: "24px" }}>
                <h2 style={{ fontSize: "20px", fontWeight: "800", color: "#0f172a", marginBottom: "24px", display: "flex", alignItems: "center", gap: "8px" }}>
                  📅 Activity Timeline
                </h2>
                
                {useHeatmap ? (
                  // STRUCTURED MONTHLY CARDS FOR QUARTERLY / YEARLY
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "24px" }}>
                    {Object.entries(groupedDates).map(([monthLabel, daysInMonth]) => (
                      <div key={monthLabel} style={{ backgroundColor: "#f8fafc", padding: "20px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                        <div style={{ fontSize: "14px", fontWeight: "800", color: "#475569", marginBottom: "16px", textTransform: "uppercase", letterSpacing: "1px" }}>
                          {monthLabel}
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {daysInMonth.map((dateStr) => {
                            const record = reportData.dailyRecords[dateStr];
                            const status = record?.status;
                            
                            let bgColor = "#e2e8f0"; // Default / No Data
                            if (record?.isOnLeave) bgColor = "#94a3b8";
                            else if (record?.isHoliday) bgColor = "#fcd34d";
                            else if (status === "Present") bgColor = "#10b981";
                            else if (status === "Absent") bgColor = "#fca5a5"; // Member Absents also render red

                            return (
                              <div key={dateStr} onMouseEnter={() => setHoveredDay(dateStr)} onMouseLeave={() => setHoveredDay(null)} style={{ position: "relative" }}>
                                <div style={{ width: "20px", height: "20px", borderRadius: "4px", backgroundColor: bgColor, cursor: "pointer", border: "1px solid rgba(0,0,0,0.05)", transition: "transform 0.1s" }} onMouseOver={(e) => e.currentTarget.style.transform = "scale(1.2)"} onMouseOut={(e) => e.currentTarget.style.transform = "scale(1)"} />
                                
                                {hoveredDay === dateStr && (
                                  <div style={{ position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)", backgroundColor: "#0f172a", color: "white", padding: "12px 16px", borderRadius: "8px", fontSize: "13px", fontWeight: "600", whiteSpace: "nowrap", zIndex: 1000, marginBottom: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
                                    <div style={{ color: "#94a3b8", fontSize: "11px", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{dateStr}</div>
                                    {status === "Present" && !record?.isHoliday && !record?.isOnLeave && (
                                      <div style={{ color: "#34d399", fontSize: "14px" }}>{isMember ? "💪 Present" : "✓ Present"}</div>
                                    )}
                                    {record?.isOnLeave && <div style={{ color: "#e2e8f0" }}>🌴 Leave: {record.leaveReason}</div>}
                                    {record?.isHoliday && <div style={{ color: "#fcd34d" }}>🎉 Holiday: {record.holidayName}</div>}
                                    {status === "Absent" && !record?.isHoliday && !record?.isOnLeave && <div style={{ color: "#fca5a5" }}>✗ Absent</div>}
                                    {status === "No Data" && !record?.isHoliday && !record?.isOnLeave && <div style={{ color: "#94a3b8" }}>— No Data</div>}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // GRID VIEW FOR MONTHLY
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: "16px" }}>
                    {reportData.dates.map((dateStr) => {
                      const day = parseInt(dateStr.split("-")[2]);
                      const record = reportData.dailyRecords[dateStr];
                      const status = record?.status;
                      const time = record?.time || record?.checkIn;

                      let bgColor, borderColor, textColor, icon;
                      if (record?.isOnLeave) { bgColor = "#f8fafc"; borderColor = "#94a3b8"; textColor = "#475569"; icon = "🌴"; }
                      else if (record?.isHoliday) { bgColor = "#fffbeb"; borderColor = "#f59e0b"; textColor = "#d97706"; icon = "🎉"; }
                      else if (status === "Present") { bgColor = "#ecfdf5"; borderColor = "#10b981"; textColor = "#059669"; icon = isMember ? "💪" : "✓"; }
                      else if (status === "Absent") {
                        bgColor = "#fef2f2"; 
                        borderColor = "#ef4444"; 
                        textColor = "#dc2626"; 
                        icon = "✗";
                      } else { bgColor = "#f8fafc"; borderColor = "#e2e8f0"; textColor = "#94a3b8"; icon = "—"; }

                      return (
                        <div key={dateStr} onMouseEnter={() => setHoveredDay(dateStr)} onMouseLeave={() => setHoveredDay(null)} style={{ position: "relative" }}>
                          <div style={{ padding: "16px 12px", borderRadius: "12px", backgroundColor: bgColor, border: `2px solid ${borderColor}`, color: textColor, textAlign: "center", cursor: "pointer", transition: "all 0.2s", minHeight: "100px", display: "flex", flexDirection: "column", justifyContent: "space-between" }} onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 10px 15px -3px rgba(0,0,0,0.1)"; }} onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
                            <div style={{ fontSize: "22px", fontWeight: "800", lineHeight: "1", color: "#0f172a" }}>{day}</div>
                            <div style={{ fontSize: "28px", fontWeight: "700", lineHeight: "1", margin: "8px 0" }}>{icon}</div>
                            <div style={{ fontSize: "11px", fontWeight: "700", opacity: 0.9, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                              {record?.isOnLeave ? "Leave" : record?.isHoliday ? "Holiday" : status === "Present" ? "Present" : status === "Absent" ? "Absent" : "No Data"}
                            </div>
                          </div>

                          {hoveredDay === dateStr && (
                            <div style={{ position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)", backgroundColor: "#0f172a", color: "white", padding: "16px", borderRadius: "12px", fontSize: "13px", fontWeight: "600", whiteSpace: "nowrap", zIndex: 1000, marginBottom: "12px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }}>
                              <div style={{ color: "#94a3b8", fontSize: "11px", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{dateStr}</div>
                              {status === "Present" && !record?.isHoliday && !record?.isOnLeave && (
                                  <>
                                    <div style={{ fontWeight: "700", marginBottom: "6px", color: "#34d399", fontSize: "15px" }}>{isMember ? "💪 Present" : "✓ Present"}</div>
                                    {time && <div style={{ fontSize: "12px", opacity: 0.9, marginTop: "4px" }}>🕐 In: {formatTime(time)}</div>}
                                    {record?.checkOut && <div style={{ fontSize: "12px", opacity: 0.9, marginTop: "2px" }}>🚪 Out: {formatTime(record.checkOut)}</div>}
                                  </>
                                )}
                              {record?.isOnLeave && <div style={{ color: "#f8fafc" }}>🌴 Leave: {record.leaveReason}</div>}
                              {record?.isHoliday && <div style={{ color: "#fcd34d" }}>🎉 Holiday: {record.holidayName}</div>}
                              {status === "Absent" && !record?.isHoliday && !record?.isOnLeave && <div style={{ color: "#fca5a5" }}>✗ Absent</div>}
                              {status === "No Data" && !record?.isHoliday && !record?.isOnLeave && <div style={{ color: "#94a3b8" }}>— No Data Available</div>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return null;
}
